import { Typography } from '@mui/material';

import { formatPeriod } from 'modules/common/components/VerifiedStatementUpload/utils/formatMonthList';
import { thisAccountLabel } from 'modules/common/components/VerifiedStatementUpload/utils/statementFilings';
import Dialog from 'modules/common/components/MUI/Dialog';
import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

interface ConfirmStatementDeletionDialogProps {
    readonly open: boolean;
    readonly onClose: () => void;
    readonly onConfirm: () => void | Promise<void>;
    readonly fileName: string;
    /** The month the row shows. Absent on a row standing for the whole file. */
    readonly period?: StatementPeriod;
    readonly accountMask: string;
    /** The removal is away; the dialog holds until it lands. */
    readonly isRemoving: boolean;
}

/**
 * Confirms a removal that deletes the file.
 *
 * A row's ✕ takes one month off one card, and the server deletes the file only once no account
 * holds anything from it — so most removals keep the file, and asking on those would train members
 * to click through this. Shown only when the removal is the file's last hold anywhere, which is
 * the one case that cannot be undone from this screen.
 */
export default function ConfirmStatementDeletionDialog({
    open,
    onClose,
    onConfirm,
    fileName,
    period = undefined,
    accountMask,
    isRemoving,
}: Readonly<ConfirmStatementDeletionDialogProps>) {
    const card = thisAccountLabel(accountMask);
    const month = period ? formatPeriod(period) : undefined;

    return (
        <Dialog
            open={open}
            onClose={isRemoving ? undefined : onClose}
            heading={`Delete ${fileName}?`}
            size="small"
            negativeAction={{ label: 'Cancel', onClick: onClose, disabled: isRemoving }}
            positiveAction={{ label: 'Delete file', onClick: onConfirm, loading: isRemoving }}>
            <Typography variant="body1">
                {month
                    ? `This removes ${month} from ${card} and deletes ${fileName}. You'd need to upload it again to use it.`
                    : `You'd need to upload it again to use it.`}
            </Typography>
        </Dialog>
    );
}
